import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker'; // DateTimePicker 대신 DatePicker 사용
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import React, { useEffect, useState } from 'react';
import { getUser } from '../../../services/authService';
import axios from '../../../utils/axiosInstance';

// dayjs 한국어 설정
dayjs.locale('ko');

// 다크 모드 스타일 상수
const darkModeStyles = {
  container: {
    p: 2,
    background: 'linear-gradient(180deg, rgba(25,25,38,0.95) 0%, rgba(25,32,48,0.95) 100%)',
    minHeight: '100vh',
  },
  paper: {
    p: 3,
    background: 'linear-gradient(145deg, rgba(35,35,50,0.9) 0%, rgba(35,45,65,0.85) 100%)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(8px)',
  },
  title: {
    color: '#fff',
    fontSize: '28px',
    fontWeight: 'bold',
    mb: 1,
    letterSpacing: '0.5px',
    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  },
  subtitle: {
    color: 'rgba(255, 255, 255, 0.7)',
    mb: 3,
  },
  formControl: {
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      color: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '& .MuiSelect-icon': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  menuItem: {
    '&.MuiMenuItem-root': {
      color: '#fff',
      backgroundColor: 'rgba(35, 35, 50, 0.95)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
      '&.Mui-selected': {
        backgroundColor: 'rgba(96, 165, 250, 0.2)',
        '&:hover': {
          backgroundColor: 'rgba(96, 165, 250, 0.3)',
        },
      },
    },
  },
  menuItemHighlight: {
    '&.MuiMenuItem-root': {
      color: '#ce93d8',
      fontWeight: 'bold',
      backgroundColor: 'rgba(35, 35, 50, 0.95)',
      '&:hover': {
        backgroundColor: 'rgba(206, 147, 216, 0.1)',
      },
      '&.Mui-selected': {
        backgroundColor: 'rgba(206, 147, 216, 0.2)',
        '&:hover': {
          backgroundColor: 'rgba(206, 147, 216, 0.3)',
        },
      },
    },
  },
  datePickerTextField: {
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: '#fff',
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    '& .MuiSvgIcon-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  searchButton: {
    backgroundColor: '#60a5fa',
    color: '#fff',
    borderRadius: '10px',
    padding: '10px 24px',
    boxShadow: '0 4px 10px rgba(33, 150, 243, 0.3)',
    fontWeight: 'bold',
    textTransform: 'none',
    transition: 'all 0.3s ease',
    width: '15%',
    '&:hover': {
      backgroundColor: '#3b82f6',
      boxShadow: '0 6px 12px rgba(30, 136, 229, 0.4)',
      transform: 'translateY(-2px)',
    },
  },
  tableContainer: {
    overflowX: 'auto',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    mb: 3,
  },
  tableHead: {
    backgroundColor: 'rgba(45, 45, 65, 0.9)',
    '& .MuiTableCell-head': {
      color: '#fff',
      fontWeight: 'bold',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    },
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: 'rgba(255, 255, 255, 0.02)',
    },
    '&:nth-of-type(even)': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      transition: 'background-color 0.3s ease',
    },
    '& .MuiTableCell-body': {
      color: '#fff',
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    },
  },
  tableRowTotal: {
    backgroundColor: 'rgba(156, 39, 176, 0.15)',
    '&:hover': {
      backgroundColor: 'rgba(156, 39, 176, 0.2)',
    },
    '& .MuiTableCell-body': {
      color: '#fff',
      fontWeight: 'bold',
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    },
  },
  tableCell: {
    color: '#fff',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
  },
  tableCellTotal: {
    color: '#ce93d8',
    fontWeight: 'bold',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
  },
  tablePagination: {
    marginTop: '12px',
    borderRadius: '12px',
    backgroundColor: 'rgba(45, 45, 65, 0.8)',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    '& .MuiTablePagination-toolbar': {
      justifyContent: 'center',
    },
    '& .MuiTablePagination-select': {
      padding: '6px 10px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: '#fff',
      fontWeight: 'bold',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
      '& .MuiSelect-icon': {
        color: 'rgba(255, 255, 255, 0.7)',
      },
    },
    '& .MuiTablePagination-actions': {
      color: '#fff',
      '& .MuiIconButton-root': {
        color: 'rgba(255, 255, 255, 0.7)',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
        '&.Mui-disabled': {
          color: 'rgba(255, 255, 255, 0.3)',
        },
      },
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
  },
  noDataBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100px',
  },
  noDataMessage: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontStyle: 'italic',
  },
  switchLabel: {
    color: 'rgba(255, 255, 255, 0.9)',
    '& .MuiSwitch-root': {
      '& .MuiSwitch-track': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
      },
      '& .MuiSwitch-switchBase': {
        color: 'rgba(255, 255, 255, 0.7)',
        '&.Mui-checked': {
          color: '#ce93d8',
        },
        '&.Mui-checked + .MuiSwitch-track': {
          backgroundColor: '#ba68c8',
          opacity: 0.5,
        },
      },
    },
  },
};

// 수정된 DatePicker 컴포넌트 - 날짜만 선택
const CustomDatePicker = ({ label, value, onChange }) => {
  return (
    <DatePicker
      label={label}
      value={value}
      onChange={(newValue) => {
        if (!newValue) return;
        console.log(`${label} 변경:`, newValue.format('YYYY-MM-DD'));
        onChange(newValue);
      }}
      format="YYYY-MM-DD"
      slotProps={{
        textField: {
          sx: darkModeStyles.datePickerTextField,
          fullWidth: true,
        },
        popper: {
          sx: {
            '& .MuiPaper-root': {
              backgroundColor: 'rgba(35, 35, 50, 0.95)',
              color: '#fff',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
              '& .MuiPickersDay-root': {
                color: '#fff',
                '&.Mui-selected': {
                  backgroundColor: '#60a5fa',
                  color: '#fff',
                },
                '&:hover': {
                  backgroundColor: 'rgba(96, 165, 250, 0.2)',
                },
              },
              '& .MuiDayCalendar-weekDayLabel': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiPickersCalendarHeader-label': {
                color: '#fff',
              },
              '& .MuiPickersCalendarHeader-switchViewButton': {
                color: '#fff',
              },
              '& .MuiIconButton-root': {
                color: '#fff',
              },
              '& .MuiPickersYear-yearButton': {
                color: '#fff',
                '&.Mui-selected': {
                  backgroundColor: '#60a5fa',
                  color: '#fff',
                },
              },
              '& .MuiPickersMonth-root': {
                color: '#fff',
                '&.Mui-selected': {
                  backgroundColor: '#60a5fa',
                },
              },
              '& .MuiPickersArrowSwitcher-button': {
                color: '#fff',
              },
              '& .MuiButtonBase-root': {
                color: '#fff',
              },
              '& .MuiButton-root': {
                color: '#60a5fa',
              },
              '& .MuiPickersDay-today': {
                border: '1px solid #60a5fa',
              },
            },
          },
        },
      }}
    />
  );
};

const MoneyDay = () => {
  const isNonMobile = useMediaQuery('(min-width:600px)');

  const [filters, setFilters] = useState({
    agentId: '',
    includeChildren: false,
    startDate: dayjs().startOf('month'), // 이번달 1일로 변경
    endDate: dayjs().endOf('day'), // 오늘까지
  });

  const [dailyData, setDailyData] = useState([]);
  const [summary, setSummary] = useState({
    total_deposit: 0,
    total_withdraw: 0,
    total_count: 0,
    net_amount: 0
  });
  const [period, setPeriod] = useState({
    start_date: '',
    end_date: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [agentList, setAgentList] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  // 현재 로그인한 사용자 정보 가져오기
  useEffect(() => {
    const user = getUser();
    if (user) {
      setCurrentUser(user);
      setFilters(prev => ({
        ...prev,
        agentId: user.id
      }));
    }
  }, []);

  // 에이전트 목록 불러오기
  useEffect(() => {
    const fetchAgentList = async () => {
      try {
        const response = await axios.get('/agent/children');
        if (response.data.success) {
          const agentTree = response.data.agent_tree;
          const agentOptions = [
            { id: agentTree.id, name: agentTree.id, level: 0 }, // 현재 로그인한 에이전트
            ...agentTree.children.map(child => ({
              id: child.id,
              name: child.id,
              level: child.level,
            })),
          ];
          setAgentList(agentOptions);
        }
      } catch (error) {
        console.error('에이전트 목록 불러오기 중 오류 발생:', error);
      }
    };

    fetchAgentList();
  }, []);

  // 데이터 가져오기
  const fetchData = async () => {
    if (!filters.agentId) {
      return;
    }
    
    setLoading(true);
    try {
      // 날짜 포맷팅
      const startDateFormatted = filters.startDate.format('YYYY-MM-DD');
      const endDateFormatted = filters.endDate.format('YYYY-MM-DD');
      
      console.log('요청 파라미터:', {
        agent_id: filters.agentId,
        start: startDateFormatted,
        end: endDateFormatted,
        daily_sum: true,
        include_children: filters.includeChildren
      });

      const response = await axios.get(`/agent/point/logs`, {
        params: {
          agent_id: filters.agentId,
          start: startDateFormatted,
          end: endDateFormatted,
          daily_sum: true,
          include_children: filters.includeChildren
        }
      });

      console.log('API 응답 데이터:', response.data);

      if (response.data) {
        setDailyData(response.data.daily_data || []);
        setSummary(response.data.summary || {
          total_deposit: 0,
          total_withdraw: 0,
          total_count: 0,
          net_amount: 0
        });
        setPeriod(response.data.period || {
          start_date: startDateFormatted,
          end_date: endDateFormatted
        });
      }
    } catch (error) {
      console.error('데이터 가져오기 중 오류 발생:', error);
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 시 초기 데이터 로드
  useEffect(() => {
    if (filters.agentId) {
      fetchData();
    }
  }, [filters.agentId]);

  // filters.includeChildren 변경 시 즉시 데이터 새로고침
  useEffect(() => {
    if (filters.agentId) {
      fetchData();
    }
  }, [filters.includeChildren]);

  const handleFilterChange = (field, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    fetchData();
  };

  // 페이지네이션 상태
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);

  // 페이지 변경 핸들러
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // 보여지는 행 개수 변경 핸들러
  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 숫자 포맷팅 함수
  const formatNumber = number => {
    if (number === null || number === undefined) return '0';
    return Number(number).toLocaleString('ko-KR');
  };

  return (
    <Box sx={darkModeStyles.container}>
      <Paper sx={darkModeStyles.paper}>
        <Typography variant="h4" sx={darkModeStyles.title}>
          일자별 통계
        </Typography>
        <Typography variant="body2" sx={darkModeStyles.subtitle}>
          소수점 자리는 첫째 자리에서 반올림 되어집니다. 매 1시간 마다 통계내용이 갱신 됩니다.
        </Typography>

        {/* 필터 영역 */}
        <Box display="flex" flexDirection={isNonMobile ? 'row' : 'column'} gap={2} sx={{ mb: 3 }}>
          <FormControl fullWidth sx={darkModeStyles.formControl}>
            <InputLabel>에이전트 아이디</InputLabel>
            <Select
              value={filters.agentId}
              onChange={e => handleFilterChange('agentId', e.target.value)}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgba(35, 35, 50, 0.95)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                  },
                },
              }}
            >
              {agentList.map(agent => (
                <MenuItem 
                  key={agent.id} 
                  value={agent.id} 
                  sx={agent.grade === 'admin' ? darkModeStyles.menuItemHighlight : darkModeStyles.menuItem}
                >
                  {/* admin이고 level이 0인 경우(자기 자신) 특별한 표시 */}
                  {currentUser?.grade === 'admin' && agent.level === 0 
                    ? '에이전트 아이디를 선택하세요' 
                    : agent.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: isNonMobile ? 'row' : 'column', gap: 2 }}>
                <CustomDatePicker label="시작 날짜" value={filters.startDate} onChange={date => handleFilterChange('startDate', date)} />
                <CustomDatePicker label="종료 날짜" value={filters.endDate} onChange={date => handleFilterChange('endDate', date)} />
              </Box>
            </LocalizationProvider>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
            sx={{
              ...darkModeStyles.searchButton,
              height: isNonMobile ? 'auto' : 56,
              alignSelf: isNonMobile ? 'flex-start' : 'stretch',
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon sx={{ fontSize: 20, mr: 1 }} />}
            {loading ? '로딩 중...' : '검색'}
          </Button>
        </Box>

        {/* 하부 합산 스위치 */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={filters.includeChildren}
                onChange={(e) => handleFilterChange('includeChildren', e.target.checked)}
                color="secondary"
              />
            }
            label="하부 합산"
            sx={darkModeStyles.switchLabel}
          />
        </Box>

        {/* 테이블 영역 */}
        <TableContainer sx={darkModeStyles.tableContainer}>
          {loading ? (
            <Box sx={darkModeStyles.loadingBox}>
              <CircularProgress sx={darkModeStyles.loadingIndicator} />
            </Box>
          ) : dailyData.length === 0 ? (
            <Box sx={darkModeStyles.noDataBox}>
              <Typography sx={darkModeStyles.noDataMessage}>데이터가 없습니다.</Typography>
            </Box>
          ) : (
            <Table stickyHeader>
              <TableHead sx={darkModeStyles.tableHead}>
                <TableRow>
                  <TableCell sx={darkModeStyles.tableCell}>일자</TableCell>
                  <TableCell sx={darkModeStyles.tableCell}>충전 금액</TableCell>
                  <TableCell sx={darkModeStyles.tableCell}>회수 금액</TableCell>
                  <TableCell sx={darkModeStyles.tableCell}>거래 건수</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dailyData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                  <TableRow key={index} sx={darkModeStyles.tableRow}>
                    <TableCell sx={darkModeStyles.tableCell}>{row.date}</TableCell>
                    <TableCell sx={darkModeStyles.tableCell}>{formatNumber(row.total_deposit)} 원</TableCell>
                    <TableCell sx={darkModeStyles.tableCell}>{formatNumber(row.total_withdraw)} 원</TableCell>
                    <TableCell sx={darkModeStyles.tableCell}>{formatNumber(row.transaction_count)} 건</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={darkModeStyles.tableRowTotal}>
                  <TableCell sx={darkModeStyles.tableCellTotal}>
                    총합{filters.includeChildren ? ' (하부 포함)' : ''}
                  </TableCell>
                  <TableCell sx={darkModeStyles.tableCellTotal}>{formatNumber(summary.total_deposit)} 원</TableCell>
                  <TableCell sx={darkModeStyles.tableCellTotal}>{formatNumber(summary.total_withdraw)} 원</TableCell>
                  <TableCell sx={darkModeStyles.tableCellTotal}>{formatNumber(summary.total_count)} 건</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
          {dailyData.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10, 20, 30, 50, 100]}
              component="div"
              count={dailyData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage=""
              sx={darkModeStyles.tablePagination}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: 'rgba(35, 35, 50, 0.95)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      '& .MuiMenuItem-root': {
                        color: '#fff',
                        padding: '8px 16px',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(96, 165, 250, 0.2)',
                        },
                      },
                    },
                  },
                },
              }}
            />
          )}
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default MoneyDay;